<template>
    <div>
        <!-- Başlık -->
        <b-card no-body class="mb-1">
            <div class="d-flex flex-wrap align-items-center justify-content-between p-1">
                <div>
                    <h4 class="mb-25 font-weight-bolder">Alacak Yaşlandırma Raporu</h4>
                    <small class="text-muted">
                        Açık alacaklar Netsis'ten anlık çekilir (aktif eczane carileri).
                        Yaşlandırma <strong>fatura kesim tarihinden</strong> hesaplanır:
                        ≤10 g taksitli ödeme açık · 11-40 g onaylı taksit açık · 40 g sonrası taksit kapalı (vade geçti).
                        <span v-if="lastUpdated"> Son güncelleme: {{ lastUpdated }}</span>
                    </small>
                </div>
                <div class="mt-1 mt-md-0">
                    <v-executer :loading="loading" :action="getData">Yenile</v-executer>
                </div>
            </div>
        </b-card>

        <!-- Uyarıcı kurgular -->
        <template v-if="!loading && rawItems.length">
            <b-alert variant="danger" :show="criticalTotal > 0" class="mb-1">
                <div class="alert-body d-flex align-items-center">
                    <span class="material-icons-round mr-1">report</span>
                    <div>
                        <strong>{{ money(criticalTotal) }}</strong> tutarındaki alacak
                        <strong>90+ gün</strong> gecikmede
                        ({{ criticalCount }} belge / {{ criticalCustomers }} cari). Acil tahsilat gerekiyor.
                    </div>
                </div>
            </b-alert>

            <b-alert variant="warning" :show="approachingTotal > 0" class="mb-1">
                <div class="alert-body d-flex align-items-center">
                    <span class="material-icons-round mr-1">hourglass_bottom</span>
                    <div>
                        <strong>{{ money(approachingTotal) }}</strong> tutarındaki alacakta vade geçti, taksit kapandı
                        (41-90 gün). Takibe alınmalı.
                    </div>
                </div>
            </b-alert>

            <b-alert variant="primary" :show="concentrationPct >= 50 && distinctCustomers > 5" class="mb-1">
                <div class="alert-body d-flex align-items-center">
                    <span class="material-icons-round mr-1">pie_chart</span>
                    <div>
                        En büyük 5 cari, toplam açık alacağın <strong>%{{ concentrationPct }}</strong>'ini oluşturuyor
                        (yoğunlaşma riski).
                    </div>
                </div>
            </b-alert>
        </template>

        <!-- Filtreler -->
        <b-card no-body class="mb-1">
            <div class="d-flex align-items-center justify-content-between p-1 border-bottom">
                <span class="font-weight-bolder d-flex align-items-center">
                    <feather-icon icon="FilterIcon" class="mr-50" /> Filtreler
                </span>
                <b-button size="sm" variant="flat-secondary" @click="clearFilters">Temizle</b-button>
            </div>
            <div class="p-1">
                <div class="row">
                    <div class="col-md-3 mb-1">
                        <label class="form-label">Plasiyer</label>
                        <v-select-list :options="plasiyerOptions" label="Visual" v-model="selectedPlasiyer" placeholder="Tümü"></v-select-list>
                    </div>
                    <div class="col-md-3 mb-1">
                        <label class="form-label">Cari Ara (kod / ad / belge)</label>
                        <input class="form-control" v-model="search" placeholder="Ara...">
                    </div>
                    <div class="col-md-2 mb-1">
                        <label class="form-label">Fatura Yaşı Gün (min - max)</label>
                        <div class="d-flex">
                            <input type="number" class="form-control mr-50" v-model.number="overdueMin" placeholder="min">
                            <input type="number" class="form-control" v-model.number="overdueMax" placeholder="max">
                        </div>
                    </div>
                    <div class="col-md-2 mb-1">
                        <label class="form-label">Min. Kalan Borç</label>
                        <input type="number" class="form-control" v-model.number="minDebt" placeholder="0">
                    </div>
                    <div class="col-md-2 mb-1">
                        <label class="form-label">Vade Durumu</label>
                        <select class="form-control" v-model="dueStatus">
                            <option value="all">Tümü</option>
                            <option value="notdue">Vadesi Geçmemiş (≤40 g)</option>
                            <option value="overdue">Vadesi Geçmiş (40 g+)</option>
                        </select>
                    </div>
                </div>
                <!-- Kova hızlı filtreleri -->
                <div class="d-flex flex-wrap mt-50">
                    <button
                        v-for="b in buckets"
                        :key="b.key"
                        class="btn btn-sm mr-50 mb-50 bucket-chip"
                        :style="bucketChipStyle(b)"
                        @click="toggleBucket(b.key)"
                    >
                        <span class="bucket-dot" :style="{ backgroundColor: colorOf(b.key) }"></span>
                        {{ b.short }}
                        <span class="bucket-chip-count">{{ bucketRawCount(b.key) }}</span>
                    </button>
                </div>
            </div>
        </b-card>

        <!-- İstatistik kutucukları -->
        <div class="row">
            <div class="col-md mb-1">
                <v-stats-box text="Toplam Açık Bakiye" variant="primary" icon="account_balance_wallet" :value="money(grandTotal)"></v-stats-box>
            </div>
            <div class="col-md mb-1">
                <v-stats-box text="Vadesi Geçmiş (40 g+)" variant="danger" icon="event_busy" :value="money(overdueTotal)" :short-text="overdueCount + ' belge'"></v-stats-box>
            </div>
            <div class="col-md mb-1">
                <v-stats-box text="Kritik (90+)" variant="danger" icon="warning" :value="money(criticalTotal)" :short-text="criticalCount + ' belge'"></v-stats-box>
            </div>
            <div class="col-md mb-1">
                <v-stats-box text="Cari Sayısı" variant="info" icon="store" :value="distinctCustomers"></v-stats-box>
            </div>
            <div class="col-md mb-1">
                <v-stats-box text="Belge Sayısı" variant="secondary" icon="receipt_long" :value="filteredItems.length"></v-stats-box>
            </div>
        </div>

        <!-- Dağılım barı (ölçeklenmiş) -->
        <b-card class="mb-1">
            <h6 class="font-weight-bolder mb-1">Yaşlandırma Dağılımı</h6>
            <div class="aging-bar" v-if="grandTotal > 0">
                <div
                    v-for="b in bucketSummary"
                    :key="b.key"
                    v-show="b.total > 0"
                    class="aging-bar-seg"
                    :style="{ width: b.pct + '%', backgroundColor: b.color }"
                    v-b-tooltip.hover
                    :title="`${b.label}: ${money(b.total)} (%${b.pct})`"
                >
                    <span v-if="b.pct >= 7">%{{ b.pct }}</span>
                </div>
            </div>
            <div v-else class="text-center text-muted py-2">Gösterilecek veri yok.</div>

            <div class="row mt-1">
                <div class="col-6 col-md mb-50" v-for="b in bucketSummary" :key="b.key + '-leg'">
                    <div class="d-flex align-items-center">
                        <span class="bucket-dot mr-50" :style="{ backgroundColor: b.color }"></span>
                        <small class="text-muted">{{ b.short }}</small>
                    </div>
                    <div class="font-weight-bolder">{{ money(b.total) }}</div>
                    <small class="text-muted">{{ b.count }} belge · %{{ b.pct }}</small>
                </div>
            </div>
        </b-card>

        <!-- Grafikler -->
        <div class="row">
            <div class="col-lg-5 mb-1">
                <b-card class="h-100 mb-0">
                    <h6 class="font-weight-bolder mb-1">Kova Bazlı Dağılım</h6>
                    <div class="chart-container">
                        <app-echart-doughnut v-if="hasData" :key="'dn-' + chartKey" :series="doughnutSeries"></app-echart-doughnut>
                        <div v-else class="text-center text-muted py-3">Veri yok</div>
                    </div>
                </b-card>
            </div>
            <div class="col-lg-7 mb-1">
                <b-card class="h-100 mb-0">
                    <h6 class="font-weight-bolder mb-1">En Yüksek 10 Cari (Kalan Borç)</h6>
                    <div class="chart-container">
                        <app-echart-bar v-if="hasData" :key="'td-' + chartKey" :option-data="topDebtorsChart"></app-echart-bar>
                        <div v-else class="text-center text-muted py-3">Veri yok</div>
                    </div>
                </b-card>
            </div>
        </div>

        <div class="row" v-if="plasiyerSummary.length > 1">
            <div class="col-12 mb-1">
                <b-card class="mb-0">
                    <h6 class="font-weight-bolder mb-1">Plasiyer Bazlı Açık Bakiye</h6>
                    <div class="chart-container">
                        <app-echart-bar :key="'pl-' + chartKey" :option-data="plasiyerChart"></app-echart-bar>
                    </div>
                </b-card>
            </div>
        </div>

        <!-- Detay tablosu -->
        <b-card no-body>
            <div class="p-1 pb-0 d-flex align-items-center justify-content-between">
                <h6 class="font-weight-bolder mb-0">Detay ({{ filteredItems.length }} belge)</h6>
            </div>
            <v-table
                ref="table"
                with-items
                :items="filteredItems"
                :fields="fields"
                pagination="client"
                :initial-per-page="20"
                :settings="false"
                :search="false"
                row-slot="out"
                :excel-callback="exportCsv"
            >
                <template #row-out="{ row }">
                    <tr>
                        <td class="text-nowrap">{{ row.CustomerCode }}</td>
                        <td>{{ row.CustomerName }}</td>
                        <td class="text-nowrap">{{ plasiyerLabel(row.PlasiyerCode) }}</td>
                        <td class="text-nowrap">{{ row.InvoiceNo }}</td>
                        <td class="text-nowrap">{{ row.Date }}</td>
                        <td class="text-nowrap">{{ row.VadeTarihi }}</td>
                        <td class="text-nowrap" :class="overdueClass(row.OverdueDays)">{{ row.OverdueDays }}</td>
                        <td class="text-nowrap">
                            <b-badge :style="badgeStyle(row.Bucket)">{{ bucketShort(row.Bucket) }}</b-badge>
                        </td>
                        <td class="text-right text-nowrap font-weight-bolder">{{ money(row.RemainingDebtValue) }}</td>
                    </tr>
                </template>
            </v-table>
        </b-card>
    </div>
</template>

<script>
import { BCard, BBadge, BAlert, BButton, VBTooltip } from 'bootstrap-vue'
import { ReportApi } from '@/api/report'
import { UserApi } from '@/api/user'
import VStatsBox from '@/components/VStatsBox.vue'
import AppEchartBar from '@/@core/components/charts/echart/AppEchartBar.vue'
import AppEchartDoughnut from '@/@core/components/charts/echart/AppEchartDoughnut.vue'
import Additional from '@/utilities/additional'
import moment from 'moment'

const BUCKETS = [
    { key: '0-10', label: 'Taksitli Ödeme Açık (≤10 g)', short: '≤10 g' },
    { key: '11-40', label: 'Onaylı Taksit Açık (11-40 g)', short: '11-40 g' },
    { key: '41-60', label: 'Vade Geçti (41-60 g)', short: '41-60 g' },
    { key: '61-90', label: 'Vade Geçti (61-90 g)', short: '61-90 g' },
    { key: '90+', label: 'Vade Geçti — Kritik (90+ g)', short: '90+ g' },
]

// Taksit penceresi (gün, vade tarihinden itibaren):
//   ≤10  → taksitli ödeme açık (yeşil, sağlıklı)
//   11-40 → onaylı taksit açık (amber, dikkat — hâlâ açık ama onay gerekiyor)
//   40+  → taksit kapalı = VADE GEÇTİ → kırmızı tonları (gecikme arttıkça koyulaşır)
const PALETTE = {
    '0-10': '#28c76f',  // yeşil — taksitli ödeme açık
    '11-40': '#ff9f43', // amber — onaylı taksit açık (dikkat)
    '41-60': '#ea5455', // kırmızı — vade geçti / taksit kapalı
    '61-90': '#c0392b', // koyu kırmızı — vade geçti
    '90+': '#7d141d',   // en koyu kırmızı — kritik gecikme
}

export default {
    directives: { 'b-tooltip': VBTooltip },
    components: {
        BCard,
        BBadge,
        BAlert,
        BButton,
        VStatsBox,
        AppEchartBar,
        AppEchartDoughnut,
    },
    data() {
        return {
            loading: false,
            lastUpdated: null,
            rawItems: [],
            buckets: BUCKETS,
            salesmanMap: {},
            chartKey: 0,
            // filtreler
            selectedPlasiyer: null,
            search: '',
            overdueMin: null,
            overdueMax: null,
            minDebt: null,
            dueStatus: 'all', // all | notdue | overdue
            selectedBuckets: [],
            fields: [
                { name: 'Cari Kod', key: 'CustomerCode', sortable: true, thClass: 'text-nowrap' },
                { name: 'Cari Adı', key: 'CustomerName', sortable: true },
                { name: 'Plasiyer', key: 'PlasiyerCode', sortable: true, thClass: 'text-nowrap' },
                { name: 'Belge No', key: 'InvoiceNo', sortable: true, thClass: 'text-nowrap' },
                { name: 'Fatura Tarihi', key: 'Date', sortable: true, thClass: 'text-nowrap' },
                { name: 'Vade', key: 'VadeTarihi', sortable: true, thClass: 'text-nowrap' },
                { name: 'Fatura Yaşı (gün)', key: 'OverdueDays', sortable: true, thClass: 'text-nowrap' },
                { name: 'Kova', key: 'Bucket', sortable: true, thClass: 'text-nowrap' },
                { name: 'Kalan Borç', key: 'RemainingDebtValue', sortable: true, thClass: 'text-right text-nowrap' },
            ],
        }
    },
    computed: {
        hasData() {
            return this.filteredItems.length > 0
        },
        plasiyerOptions() {
            const codes = [...new Set(this.rawItems.map(i => i.PlasiyerCode).filter(Boolean))]
            const opts = codes.sort().map(c => ({ code: c, Visual: this.plasiyerLabel(c) }))
            opts.unshift({ code: null, Visual: 'Tümü' })
            return opts
        },
        filteredItems() {
            const txt = (this.search || '').toLocaleLowerCase('tr-TR').trim()
            const oMin = this.numOrNull(this.overdueMin)
            const oMax = this.numOrNull(this.overdueMax)
            const mDebt = this.numOrNull(this.minDebt)
            return this.rawItems.filter(i => {
                if (this.selectedPlasiyer && this.selectedPlasiyer.code && i.PlasiyerCode !== this.selectedPlasiyer.code) return false
                if (this.selectedBuckets.length && !this.selectedBuckets.includes(i.Bucket)) return false
                // Vade eşiği 40 gün: 0-40 vadesi geçmemiş, 41+ vadesi geçmiş.
                if (this.dueStatus === 'overdue' && i.OverdueDays <= 40) return false
                if (this.dueStatus === 'notdue' && i.OverdueDays > 40) return false
                if (oMin != null && i.OverdueDays < oMin) return false
                if (oMax != null && i.OverdueDays > oMax) return false
                if (mDebt != null && i.RemainingDebtValue < mDebt) return false
                if (txt) {
                    const hay = `${i.CustomerCode} ${i.CustomerName} ${i.InvoiceNo}`.toLocaleLowerCase('tr-TR')
                    if (!hay.includes(txt)) return false
                }
                return true
            })
        },
        grandTotal() {
            return this.sum(this.filteredItems)
        },
        overdueItems() {
            // Vadesi geçmiş = 40 günü aşmış (41. gün ve sonrası).
            return this.filteredItems.filter(i => i.OverdueDays > 40)
        },
        overdueTotal() {
            return this.sum(this.overdueItems)
        },
        overdueCount() {
            return this.overdueItems.length
        },
        criticalItems() {
            return this.filteredItems.filter(i => i.Bucket === '90+')
        },
        criticalTotal() {
            return this.sum(this.criticalItems)
        },
        criticalCount() {
            return this.criticalItems.length
        },
        criticalCustomers() {
            return new Set(this.criticalItems.map(i => i.CustomerCode)).size
        },
        approachingTotal() {
            // Vade geçmiş ama henüz kritik (90+) değil: 41-90 gün bandı.
            return this.sum(this.filteredItems.filter(i => i.Bucket === '41-60' || i.Bucket === '61-90'))
        },
        distinctCustomers() {
            return new Set(this.filteredItems.map(i => i.CustomerCode)).size
        },
        bucketSummary() {
            const total = this.grandTotal || 1
            return BUCKETS.map(b => {
                const items = this.filteredItems.filter(i => i.Bucket === b.key)
                const t = this.sum(items)
                return {
                    ...b,
                    color: this.colorOf(b.key),
                    total: t,
                    count: items.length,
                    pct: Math.round((t / total) * 1000) / 10,
                }
            })
        },
        customerTotals() {
            const map = {}
            this.filteredItems.forEach(i => {
                if (!map[i.CustomerCode]) map[i.CustomerCode] = { code: i.CustomerCode, name: i.CustomerName, total: 0 }
                map[i.CustomerCode].total += i.RemainingDebtValue
            })
            return Object.values(map).sort((a, b) => b.total - a.total)
        },
        topDebtors() {
            return this.customerTotals.slice(0, 10)
        },
        concentrationPct() {
            if (this.grandTotal <= 0) return 0
            const top5 = this.customerTotals.slice(0, 5).reduce((a, b) => a + b.total, 0)
            return Math.round((top5 / this.grandTotal) * 100)
        },
        plasiyerSummary() {
            const map = {}
            this.filteredItems.forEach(i => {
                const c = i.PlasiyerCode || '—'
                if (!map[c]) map[c] = { code: c, total: 0 }
                map[c].total += i.RemainingDebtValue
            })
            return Object.values(map).sort((a, b) => b.total - a.total)
        },
        doughnutSeries() {
            return [{
                name: 'Kova Dağılımı',
                type: 'pie',
                radius: ['45%', '70%'],
                avoidLabelOverlap: true,
                label: { show: false },
                labelLine: { show: false },
                data: this.bucketSummary.filter(b => b.total > 0).map(b => ({
                    value: Math.round(b.total * 100) / 100,
                    name: b.short,
                    itemStyle: { color: b.color },
                })),
            }]
        },
        topDebtorsChart() {
            const top = this.topDebtors
            return {
                tooltip: {
                    trigger: 'axis',
                    axisPointer: { type: 'shadow' },
                    formatter: p => `${p[0].axisValue}<br/>${p[0].marker} ${this.money(p[0].value)}`,
                },
                legend: { show: false },
                grid: { left: '10px', right: '10px', top: '10px', bottom: '5px', containLabel: true },
                xAxis: [{ type: 'category', data: top.map(t => this.truncate(t.name, 14)), axisLabel: { rotate: 35, fontSize: 9, interval: 0 }, axisTick: { alignWithLabel: true } }],
                yAxis: [{ type: 'value', axisLabel: { formatter: this.axisFmt, fontSize: 11 } }],
                series: [{ name: 'Kalan Borç', type: 'bar', data: top.map(t => Math.round(t.total * 100) / 100), barCategoryGap: '25%', itemStyle: { borderRadius: [4, 4, 0, 0], color: '#7367f0' } }],
            }
        },
        plasiyerChart() {
            const list = this.plasiyerSummary
            return {
                tooltip: {
                    trigger: 'axis',
                    axisPointer: { type: 'shadow' },
                    formatter: p => `${p[0].axisValue}<br/>${p[0].marker} ${this.money(p[0].value)}`,
                },
                legend: { show: false },
                grid: { left: '10px', right: '10px', top: '10px', bottom: '5px', containLabel: true },
                xAxis: [{ type: 'category', data: list.map(p => this.plasiyerLabel(p.code)), axisLabel: { rotate: 25, fontSize: 10, interval: 0 } }],
                yAxis: [{ type: 'value', axisLabel: { formatter: this.axisFmt, fontSize: 11 } }],
                series: [{ name: 'Açık Bakiye', type: 'bar', data: list.map(p => Math.round(p.total * 100) / 100), barCategoryGap: '30%', itemStyle: { borderRadius: [4, 4, 0, 0], color: '#00cfe8' } }],
            }
        },
    },
    watch: {
        filteredItems() {
            this.chartKey++
        },
    },
    mounted() {
        this.getSalesman()
        this.getData()
    },
    methods: {
        getData() {
            this.loading = true
            return ReportApi.ReceivableAgingReport().then(res => {
                this.rawItems = (res.data.Data && res.data.Data.Items) || []
                this.lastUpdated = moment().format('DD.MM.YYYY HH:mm')
                this.chartKey++
            }).finally(() => {
                this.loading = false
            })
        },
        getSalesman() {
            UserApi.SalesmanList().then(res => {
                const map = {}
                ;(res.data.Data || []).forEach(s => {
                    if (s.SalesPersonCode) map[s.SalesPersonCode] = s.FullName
                })
                this.salesmanMap = map
            }).catch(() => {})
        },
        sum(arr) {
            return arr.reduce((a, b) => a + (b.RemainingDebtValue || 0), 0)
        },
        numOrNull(v) {
            if (v === '' || v === null || v === undefined) return null
            const n = Number(v)
            return isNaN(n) ? null : n
        },
        money(v) {
            return Number(v || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₺'
        },
        axisFmt(val) {
            if (val >= 1000000) return (val / 1000000).toFixed(1) + 'M'
            if (val >= 1000) return (val / 1000).toFixed(0) + 'K'
            return val
        },
        truncate(s, n) {
            if (!s) return ''
            return s.length > n ? s.slice(0, n) + '…' : s
        },
        plasiyerLabel(code) {
            if (!code) return '—'
            if (code === '00') return '00 - Merkez'
            const name = this.salesmanMap[code]
            return name ? `${code} - ${name}` : code
        },
        bucketDef(key) {
            return BUCKETS.find(b => b.key === key) || { short: key }
        },
        colorOf(key) {
            return PALETTE[key] || '#82868b'
        },
        bucketShort(key) {
            return this.bucketDef(key).short
        },
        badgeStyle(key) {
            return { backgroundColor: this.colorOf(key), color: '#fff' }
        },
        overdueClass(days) {
            // ≤10 g taksitli ödeme açık (yeşil), 11-40 g onaylı taksit açık (amber/dikkat),
            // 40 g sonrası taksit kapalı = VADE GEÇTİ → kırmızı.
            if (days > 40) return 'text-danger font-weight-bolder'
            if (days > 10) return 'text-warning font-weight-bolder'
            return 'text-success'
        },
        bucketRawCount(key) {
            return this.rawItems.filter(i => i.Bucket === key).length
        },
        toggleBucket(key) {
            const idx = this.selectedBuckets.indexOf(key)
            if (idx === -1) this.selectedBuckets.push(key)
            else this.selectedBuckets.splice(idx, 1)
        },
        bucketChipStyle(b) {
            const color = this.colorOf(b.key)
            const active = this.selectedBuckets.includes(b.key)
            return active
                ? { backgroundColor: color, color: '#fff', borderColor: color }
                : { backgroundColor: 'transparent', color: 'inherit', border: '1px solid rgba(130,134,139,0.4)' }
        },
        clearFilters() {
            this.selectedPlasiyer = null
            this.search = ''
            this.overdueMin = null
            this.overdueMax = null
            this.minDebt = null
            this.dueStatus = 'all'
            this.selectedBuckets = []
        },
        exportCsv() {
            const headers = ['Cari Kod', 'Cari Adı', 'Plasiyer', 'Belge No', 'Fatura Tarihi', 'Vade', 'Fatura Yaşı (gün)', 'Kova', 'Kalan Borç']
            const rows = this.filteredItems.map(i => [
                i.CustomerCode,
                i.CustomerName,
                this.plasiyerLabel(i.PlasiyerCode),
                i.InvoiceNo,
                i.Date,
                i.VadeTarihi,
                i.OverdueDays,
                this.bucketShort(i.Bucket),
                i.RemainingDebtValue,
            ])
            const escape = v => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`
            const csv = [headers, ...rows].map(r => r.map(escape).join(';')).join('\r\n')
            const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
            Additional.DownloadFile(blob, `AlacakYaslandirma_${moment().format('YYYYMMDD_HHmmss')}.csv`)
        },
    },
}
</script>

<style scoped>
.chart-container {
    width: 100%;
    height: 300px;
}
.chart-container >>> .echarts {
    width: 100% !important;
    height: 100% !important;
}
.aging-bar {
    display: flex;
    width: 100%;
    height: 26px;
    border-radius: 6px;
    overflow: hidden;
}
.aging-bar-seg {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 2px;
    color: #fff;
    font-size: 11px;
    font-weight: 600;
    transition: width 0.4s ease;
    white-space: nowrap;
}
.bucket-dot {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
}
.bucket-chip {
    display: inline-flex;
    align-items: center;
    border-radius: 14px;
    font-size: 12px;
    font-weight: 600;
}
.bucket-chip .bucket-dot {
    margin-right: 5px;
}
.bucket-chip-count {
    margin-left: 6px;
    background: rgba(0, 0, 0, 0.12);
    border-radius: 10px;
    padding: 0 6px;
    font-size: 11px;
}
</style>
